// adminGroupChatPageHelpers — AdminGroupChatPage.js から純粋な定数/ヘルパを分離（挙動不変）。
// 状態非依存のモジュールレベル純粋部分（アセットURL/アバター/既読マージ等）。

import { buildChatAssetUrl, appendVariant } from '../../common/chat/composer/chatAssetUtils';
import { getGroupNotePreviewText } from '../../common/chat/notes/groupNoteMessageUtils';
import { getGroupArtistAvatarStyle, getGroupAssigneeAvatarStyle, getGroupArtistPictureKey } from '../../common/chat/members/groupParticipantsPanelConfigUtils';
import { pickRealtimeReadCount } from '../../common/chat/reactions/readReceiptRealtimeUtils';

export const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
export const buildApiUrl = (path) => (API_URL ? `${API_URL}${path}` : path);
export const buildAdminGroupNotesListUrl = (threadId, offset = 0, limit = 20) =>
  buildApiUrl(`/api/admin/group-chat-notes/${threadId}?offset=${offset}&limit=${limit}`);
export const MESSAGE_IMAGE_PATH = process.env.REACT_APP_MESSAGE_IMAGE_PATH || '/api/images/messages';
export const MESSAGE_FILE_PATH = process.env.REACT_APP_MESSAGE_FILE_PATH || '/api/files/message_files';
export const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
export const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH || '/api/images/admin_profiles';
export const GROUP_CHAT_ICON_IMAGE_PATH =
  process.env.REACT_APP_GROUP_CHAT_ICON_IMAGE_PATH || '/api/images/group_chat_icons';
export const EVENT_FLYER_IMAGE_PATH = '/api/images/flyers';
export const GROUP_CHAT_ASSIGNEE_SEARCH_STORAGE_KEY = 'admin:group-chat:assignee-search:v1';
export const buildAssetUrl = (root, file) => buildChatAssetUrl({ apiUrl: API_URL, root, file });
export const GROUP_TARGET_ROW_CLASSES = {
  root: 'artist-list-item group-chat-page__targetRow',
  left: 'artist-item-left group-chat-page__targetMedia',
  center: 'artist-item-center',
  nameLine: 'artist-name-line',
  name: 'artist-name',
  preview: 'artist-last-preview',
  below: 'artist-item-below',
  right: 'artist-item-right',
  time: 'artist-time',
  badge: 'new-message-badge',
};

export const fmtDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getThreadModeLabel = (mode) => (mode === 'artist_group_linked' ? 'グループ連動' : '手動');
export const getThreadStatusLabel = (status) => (status === 'open' ? '公開中' : 'クローズ');
export const getThreadPreviewText = (thread) => {
  const message = thread?.latest_message;
  if (!message) return '';
  return getGroupNotePreviewText(message) || getGroupMessagePreviewText(message) || 'メッセージを送信しました';
};

export const getArtistAvatarStyle = (artist) =>
  getGroupArtistAvatarStyle({
    artist,
    buildAssetUrl,
    appendVariant,
    artistProfilePath: ARTIST_PROFILE_IMAGE_PATH,
  });

export const getAdminAvatarStyle = (admin) =>
  getGroupAssigneeAvatarStyle({
    admin,
    buildAssetUrl,
    appendVariant,
    adminProfilePath: ADMIN_PROFILE_IMAGE_PATH,
  });

export const getArtistPictureKey = (artist) => getGroupArtistPictureKey(artist);

export const getGroupChatIconStyle = (thread) => {
  const root = thread?.icon_file ? GROUP_CHAT_ICON_IMAGE_PATH : EVENT_FLYER_IMAGE_PATH;
  const file = thread?.icon_file || thread?.linked_event_flyer_front_url;
  const url = appendVariant(buildAssetUrl(root, file), 'proxy');
  return url
    ? {
        backgroundImage: `url(${url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : { backgroundImage: 'url(/group-chat-icons/note.png)', backgroundSize: 'cover', backgroundPosition: 'center' };
};

export const hasGroupChatDisplayImage = (thread) => Boolean(getGroupChatIconStyle(thread)?.backgroundImage);

export const GROUP_CHAT_PAGE_SIZE = 50;
export const GROUP_CHAT_MAX_FILE_BYTES = 100 * 1024 * 1024;
export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const firstNonEmpty = (...values) => {
  for (const value of values) {
    const text = typeof value === 'string' ? value.trim() : '';
    if (text) return text;
  }
  return '';
};

export const normalizeRealtimePayload = (payload = {}) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const nested = source.message && typeof source.message === 'object' ? source.message : {};
  return { ...source, message: nested };
};

export const pickRealtimeThreadId = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  return firstNonEmpty(
    source.threadId,
    source.thread_id,
    source.chatThreadId,
    source.chat_thread_id,
    source.groupThreadId,
    source.group_thread_id,
    source.message?.thread_id,
    source.message?.threadId
  );
};

export const pickRealtimeCreatedAt = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  return firstNonEmpty(source.createdAt, source.created_at, source.message?.created_at) || new Date().toISOString();
};

export const getGroupMessagePreviewText = (message) => {
  if (!message) return '';
  const format = String(message.message_format || '').toLowerCase();
  if (format === 'image') return '画像を送信しました';
  if (format === 'file') return message.file_original_name || message.file_file_name || 'ファイルを送信しました';
  return String(message.content || '').replace(/\s+/g, ' ').trim();
};

export const buildRealtimeThreadLatestMessage = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  const format = String(
    source.messageFormat ||
      source.message_format ||
      source.format ||
      source.message?.message_format ||
      'text'
  ).trim().toLowerCase();
  const createdAt = pickRealtimeCreatedAt(source);
  const senderType = String(
    source.senderType ||
      source.sender_type ||
      source.message?.sender_type ||
      ''
  ).trim().toLowerCase() || 'system';
  const senderId = firstNonEmpty(
    source.senderId,
    source.sender_id,
    source.message?.admin_user_id,
    source.message?.artist_user_id
  );
  const preview = firstNonEmpty(
    source.preview,
    source.messagePreview,
    source.message_preview,
    source.content,
    source.text,
    source.message?.content,
    format === 'image' ? '画像を送信しました' : '',
    format === 'file' ? 'ファイルを送信しました' : '',
    '新着メッセージがあります'
  );
  const messageId = firstNonEmpty(source.messageId, source.message_id, source.message?.id, source.message?.message_id);
  return {
    ...source.message,
    topic: source.topic || source.message?.topic,
    topic_id: source.topicId || source.topic_id || source.message?.topic_id,
    topic_name: source.topicName || source.topic_name || source.message?.topic_name,
    id: messageId || `realtime-${createdAt}`,
    message_id: messageId || `realtime-${createdAt}`,
    message_format: format,
    sender_type: senderType,
    admin_user_id: senderType === 'admin' ? senderId : null,
    artist_user_id: senderType === 'artist' ? senderId : null,
    created_at: createdAt,
    content: format === 'text' ? preview : '',
    file_original_name: firstNonEmpty(
      source.fileOriginalName,
      source.file_original_name,
      source.message?.file_original_name,
      source.message?.fileOriginalName
    ),
    read_count: pickRealtimeReadCount(payload, 1),
    reactions: Array.isArray(source.message?.reactions) ? source.message.reactions : [],
  };
};

export const buildRealtimeThreadTextMessage = (payload = {}) => {
  const source = normalizeRealtimePayload(payload);
  if (source.question) return null;
  const format = String(
    source.messageFormat ||
      source.message_format ||
      source.format ||
      source.message?.message_format ||
      'text'
  ).trim().toLowerCase();
  const messageId = firstNonEmpty(source.messageId, source.message_id, source.message?.id, source.message?.message_id);
  const content = firstNonEmpty(source.content, source.text, source.preview, source.messagePreview, source.message?.content);
  if (format !== 'text' || !messageId || !content) return null;
  return {
    ...buildRealtimeThreadLatestMessage(payload),
    id: messageId,
    message_id: messageId,
    content,
    message_format: 'text',
    mentions: Array.isArray(source.mentions)
      ? source.mentions
      : (Array.isArray(source.message?.mentions) ? source.message.mentions : []),
  };
};
